import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MatchCard } from "@/components/MatchCard";
import { SpotlightStrip } from "@/components/SpotlightStrip";
import { SkeletonCard, Empty } from "@/components/States";
import { Heart } from "lucide-react";

const TABS = [
  ["recommended", "Recommended"],
  ["ai", "AI Matches"],
  ["preferred", "Preferred"],
  ["twoway", "2-Way"],
  ["reverse", "Reverse"],
  ["broader", "Broader"],
  ["new", "New"],
  ["who_viewed_me", "Who Viewed Me"],
  ["who_shortlisted_me", "Who Shortlisted Me"],
  ["recently_viewed", "Recently Viewed"],
];

export default function Matches() {
  const { category } = useParams();
  const nav = useNavigate();
  const active = category || "recommended";
  const [state, setState] = useState({ loading: true, results: [], locked: false, message: "" });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/matches/${active}`);
      setState({ loading: false, results: data.results, locked: data.locked, message: data.message });
    } catch {
      setState({ loading: false, results: [], locked: false, message: "Unable to load matches right now." });
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const onChanged = (uid) => setState((s) => ({ ...s, results: s.results.filter((r) => r.user_id !== uid) }));

  return (
    <div className="space-y-5" data-testid="matches-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-[#58111A]">Match Center</h1>
        <p className="text-sm text-[#605255]">Explore matches curated from your preferences and compatibility.</p>
      </div>
      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1 -mx-1 px-1">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => nav(`/app/matches/${key}`)} data-testid={`match-tab-${key}`}
            className={`shrink-0 text-sm font-medium px-4 py-2 rounded-full border transition ${active === key ? "bg-[#781826] text-white border-[#781826]" : "bg-white border-[#E8DFD1] text-[#605255] hover:border-[#C59B27]"}`}>
            {label}
          </button>
        ))}
      </div>

      <SpotlightStrip />

      {state.loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}</div>
      ) : state.locked ? (
        <Empty icon={Heart} title="Available with Premium" message={state.message || "Upgrade to see who viewed you."}
          action={<a href="/app/subscription" className="btn-primary">Upgrade for ₹150/month</a>} />
      ) : state.results.length === 0 ? (
        <Empty icon={Heart} title="No matches here yet"
          message="We couldn't find enough matches for this category. Try broadening your preferences or use AI Search."
          action={<div className="flex gap-2"><a href="/app/preferences" className="btn-outline">Broaden Preferences</a><a href="/app/search" className="btn-primary">Try Search</a></div>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {state.results.map((p) => <MatchCard key={p.user_id} p={p} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}
